/**
 * DETAIL-7C.4 — Global Saved Items page (Diamonds V1).
 *
 * One store: window.DiamondSaved / ivanovSavedDiamonds.
 * Full page hydrates current shopper-facing data via IvanovAPI.getDiamond.
 * Header preview remains snapshot-only. Does not introduce a second
 * purchase CTA, category filters, sort UI, Compare, or email hinting.
 */
(function (global) {
  "use strict";

  if (global.DiamondSavedItems && global.DiamondSavedItems.HYDRATION_CONCURRENCY === 3) {
    return;
  }

  var PAGE_PATH = "/pages/saved-items";
  var SEARCH_PATH = "/pages/diamond-search";
  var DETAIL_PATH = "/pages/diamond-details";
  var CHANGE_EVENT = "ivanov:saved-diamonds-change";
  var HYDRATION_CONCURRENCY = 3;
  var CUT_LABELS = {
    ID: "Ideal",
    EX: "Excellent",
    VG: "Very Good",
    GD: "Good",
    FR: "Fair",
  };
  var HEART_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"></path>' +
    "</svg>";
  var FALLBACK_SVG =
    '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M16 24 L32 8 L48 24 L32 58 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M16 24 L48 24 M25 24 L32 58 M39 24 L32 58 M22 24 L32 32 L42 24" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>' +
    "</svg>";

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function isMeaningful(value) {
    var normalized = String(value == null ? "" : value).trim();
    if (!normalized) return false;
    var upper = normalized.toUpperCase();
    return upper !== "NONE" && upper !== "N/A" && upper !== "NA" && upper !== "-" && upper !== "\u2014";
  }

  function toTitleCase(value) {
    return String(value)
      .toLowerCase()
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function isAbortError(err) {
    if (!err) return false;
    if (err.name === "AbortError") return true;
    return !!(err.cause && err.cause.name === "AbortError");
  }

  function savedApi() {
    return global.DiamondSaved || null;
  }

  function headerApi() {
    return global.DiamondHeaderSaved || null;
  }

  function detailApi() {
    return global.DiamondDetail || null;
  }

  function snapshotOf(item) {
    return item && item.snapshot && typeof item.snapshot === "object" ? item.snapshot : {};
  }

  function displaySource(item, diamond) {
    if (diamond && typeof diamond === "object") return diamond;
    return snapshotOf(item);
  }

  function formatCarat(carat) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatCarat === "function") {
      var fromDetail = Detail.formatCarat(carat);
      if (fromDetail) return fromDetail;
    }
    var n = Number(carat);
    return Number.isFinite(n) ? n.toFixed(2) : "";
  }

  function formatShape(shape) {
    if (!isMeaningful(shape)) return "";
    return toTitleCase(String(shape).replace(/[_-]+/g, " "));
  }

  function formatOrigin(origin) {
    var value = String(origin == null ? "" : origin).trim().toLowerCase();
    if (value === "lab-grown" || value === "labgrown" || value === "lab_grown") {
      return "Lab Grown";
    }
    if (value === "natural") return "Natural";
    return value ? toTitleCase(value.replace(/[_-]+/g, " ")) : "";
  }

  function formatIdentity(source) {
    var Header = headerApi();
    var Detail = detailApi();
    source = source || {};
    if (Detail && typeof Detail.formatStoneIdentity === "function") {
      var fromDetail = Detail.formatStoneIdentity(source);
      if (fromDetail) return fromDetail;
    }
    if (Header && typeof Header.formatIdentity === "function") {
      var fromHeader = Header.formatIdentity({ snapshot: source });
      if (fromHeader) return fromHeader;
    }
    var caratLabel = formatCarat(source.carat);
    var shapeLabel = formatShape(source.shape);
    var originLabel = formatOrigin(source.origin);
    return [caratLabel && caratLabel + " ct", shapeLabel, originLabel, "Diamond"]
      .filter(Boolean)
      .join(" ");
  }

  function formatShortIdentity(source) {
    var Header = headerApi();
    source = source || {};
    if (Header && typeof Header.formatShortIdentity === "function") {
      var fromHeader = Header.formatShortIdentity({ snapshot: source });
      if (fromHeader) return fromHeader;
    }
    var caratLabel = formatCarat(source.carat);
    var shapeLabel = formatShape(source.shape);
    return [caratLabel && caratLabel + " ct", shapeLabel].filter(Boolean).join(" ");
  }

  function formatCutSegment(cut) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatCutSegment === "function") {
      return Detail.formatCutSegment(cut) || "";
    }
    if (!isMeaningful(cut)) return "";
    var code = String(cut).trim().toUpperCase();
    if (code === "FANCY") return "";
    var label = CUT_LABELS[code] || toTitleCase(cut);
    if (!label) return "";
    if (/\bcut$/i.test(label)) return label;
    return label + " Cut";
  }

  function formatColorSegment(color) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatColorSegment === "function") {
      return Detail.formatColorSegment(color) || "";
    }
    return isMeaningful(color) ? String(color).trim() + " Color" : "";
  }

  function formatClaritySegment(clarity) {
    var Detail = detailApi();
    if (Detail && typeof Detail.formatClaritySegment === "function") {
      return Detail.formatClaritySegment(clarity) || "";
    }
    return isMeaningful(clarity) ? String(clarity).trim() + " Clarity" : "";
  }

  function formatLab(lab) {
    var Detail = detailApi();
    if (Detail && typeof Detail.gradingLab === "function") {
      return Detail.gradingLab({ lab: lab }) || "";
    }
    return isMeaningful(lab) ? String(lab).trim() : "";
  }

  function formatGrading(source) {
    var Header = headerApi();
    var Detail = detailApi();
    source = source || {};
    if (Detail && typeof Detail.formatSpecLine === "function") {
      return Detail.formatSpecLine(source) || "";
    }
    if (Header && typeof Header.formatGrading === "function") {
      return Header.formatGrading({ snapshot: source }) || "";
    }
    return [formatCutSegment(source.cut), formatColorSegment(source.color), formatClaritySegment(source.clarity), formatLab(source.lab)]
      .filter(isMeaningful)
      .join(" \u00b7 ");
  }

  function formatPrice(source) {
    var Header = headerApi();
    var Detail = detailApi();
    var value = source && typeof source === "object" ? source.retailPriceUsd : source;
    if (Detail && typeof Detail.formatPriceUsd === "function") {
      return Detail.formatPriceUsd(value) || "";
    }
    if (Header && typeof Header.formatPrice === "function" && source && typeof source === "object") {
      return Header.formatPrice({ snapshot: source }) || "";
    }
    if (value == null || value === "") return "";
    var n = Number(value);
    if (!Number.isFinite(n)) return "";
    return "$" + Math.round(n).toLocaleString("en-US");
  }

  function formatRemoveLabel(source) {
    var shortId = formatShortIdentity(source) || "diamond";
    return "Remove " + shortId + " diamond from saved items";
  }

  function formatCountLabel(count) {
    var n = Number(count) || 0;
    if (n === 1) return "1 saved item";
    return n + " saved items";
  }

  function bareUuid(id) {
    var IvanovAPI = global.IvanovAPI;
    if (IvanovAPI && typeof IvanovAPI.normalizeDiamondId === "function") {
      try {
        return IvanovAPI.normalizeDiamondId(id).bareUuid || "";
      } catch (err) {
        // Fall through.
      }
    }
    var Saved = savedApi();
    if (Saved && typeof Saved.normalizeId === "function") {
      return String(Saved.normalizeId(id) || "").replace(/^DIAMOND\//i, "");
    }
    return String(id == null ? "" : id).replace(/^DIAMOND\//i, "").trim();
  }

  function canonicalId(id) {
    var Saved = savedApi();
    if (Saved && typeof Saved.normalizeId === "function") {
      return Saved.normalizeId(id) || "";
    }
    var bare = bareUuid(id);
    return bare ? "DIAMOND/" + String(bare).toLowerCase() : "";
  }

  function detailUrl(id) {
    var Header = headerApi();
    var Detail = detailApi();
    if (Detail && typeof Detail.buildUrl === "function") {
      try {
        return Detail.buildUrl(id);
      } catch (err) {
        // Fall through.
      }
    }
    if (Header && typeof Header.detailUrl === "function") {
      try {
        return Header.detailUrl(id);
      } catch (err) {
        // Fall through.
      }
    }
    var bare = bareUuid(id);
    return bare ? DETAIL_PATH + "?id=" + encodeURIComponent(bare) : "";
  }

  function availabilityState(diamond) {
    if (!diamond || typeof diamond !== "object") return null;
    if (diamond.available === false || diamond.availability === "unavailable") {
      return "unavailable";
    }
    if (diamond.available === true || diamond.availability === "available") {
      return "available";
    }
    return null;
  }

  function isUnavailable(diamond) {
    return availabilityState(diamond) === "unavailable";
  }

  function isGenuine404(err) {
    return !!(err && Number(err.status) === 404);
  }

  function isTransientFailure(err) {
    if (!err || isAbortError(err) || isGenuine404(err)) return false;
    var status = Number(err.status);
    if (!status) return true;
    return status >= 500 || status === 408 || status === 429;
  }

  function resolveImage(diamond, snapshot) {
    var media = diamond && diamond.media && typeof diamond.media === "object" ? diamond.media : null;
    if (media && media.image) return String(media.image);
    if (diamond && diamond.image) return String(diamond.image);
    if (snapshot && snapshot.image) return String(snapshot.image);
    return "";
  }

  function leakedInternal(value) {
    return /supplierCost|wholesale|nivoda|compareScore|costUsd|confidence|markup/i.test(String(value || ""));
  }

  function currentItems() {
    var Saved = savedApi();
    if (!Saved || typeof Saved.list !== "function") return [];
    try {
      return Saved.list() || [];
    } catch (err) {
      return [];
    }
  }

  function currentCount() {
    var Saved = savedApi();
    if (Saved && typeof Saved.count === "function") {
      try {
        return Saved.count();
      } catch (err) {
        // Fall through.
      }
    }
    return currentItems().length;
  }

  function displayItems(items) {
    var list = Array.isArray(items) ? items.slice() : [];
    return list.reverse();
  }

  function viewModel(item, hydration) {
    hydration = hydration || {};
    var diamond = hydration.diamond || null;
    var snap = snapshotOf(item);
    var status = hydration.status || "loading";
    var source = status === "current" || status === "unavailable" ? displaySource(item, diamond) : snap;
    var availability = status === "current" || status === "unavailable" ? availabilityState(diamond) : null;
    if (status === "unavailable") availability = "unavailable";
    return {
      id: item && item.id ? String(item.id) : "",
      status: status,
      identity: formatIdentity(source),
      shortIdentity: formatShortIdentity(source),
      grading: formatGrading(source),
      price: formatPrice(source),
      image: resolveImage(diamond, snap),
      availability: availability,
      href: detailUrl(item && item.id),
      removeLabel: formatRemoveLabel(source),
    };
  }

  function statusCopy(model) {
    if (!model) return "";
    if (model.status === "transient") return "Details temporarily unavailable";
    if (model.availability === "unavailable") return "Unavailable";
    if (model.availability === "available") return "Available";
    return "";
  }

  function renderEmptyHtml() {
    return (
      '<div class="saved-items__empty" data-saved-empty>' +
      '<p class="saved-items__empty-title">You haven\'t saved anything yet.</p>' +
      '<p class="saved-items__empty-copy">Save diamonds while you browse and they\'ll appear here.</p>' +
      '<a class="saved-items__explore" href="' +
      SEARCH_PATH +
      '">EXPLORE DIAMONDS</a>' +
      "</div>"
    );
  }

  function renderIntroMetaHtml(count) {
    if (!count) return "";
    return (
      '<p class="saved-items__lede">Review the pieces you\'ve saved and return to them whenever you\'re ready.</p>' +
      '<p class="saved-items__count" data-saved-items-count>' +
      escapeHtml(formatCountLabel(count)) +
      "</p>"
    );
  }

  function renderCardHtml(model) {
    model = model || {};
    var identity = model.identity || "Diamond";
    var shortId = model.shortIdentity || identity;
    var grading = model.grading || "";
    var price = model.price || "";
    var href = model.href || "";
    var image = model.image || "";
    var status = statusCopy(model);
    var alt = (shortId || identity || "Diamond") + (/\bdiamond$/i.test(shortId || identity || "") ? "" : " diamond");
    var classes = "saved-items__card";
    if (model.status === "loading") classes += " is-loading";
    if (model.availability === "unavailable") classes += " is-unavailable";
    if (model.status === "transient") classes += " is-transient";
    var media =
      (image
        ? '<img data-saved-image src="' +
          escapeHtml(image) +
          '" alt="' +
          escapeHtml(alt) +
          '" width="360" height="360" loading="lazy" decoding="async">'
        : "") +
      '<span class="saved-items__fallback" data-saved-fallback' +
      (image ? " hidden" : "") +
      ">" +
      FALLBACK_SVG +
      "</span>";
    return (
      '<article class="' +
      classes +
      '" data-saved-card data-diamond-id="' +
      escapeHtml(model.id) +
      '" data-saved-status="' +
      escapeHtml(model.status || "") +
      '">' +
      '<div class="saved-items__media-wrap">' +
      '<span class="saved-items__media">' +
      media +
      "</span>" +
      '<button type="button" class="saved-items__heart is-active" data-saved-remove data-diamond-id="' +
      escapeHtml(model.id) +
      '" aria-pressed="true" aria-label="' +
      escapeHtml(model.removeLabel || formatRemoveLabel({ identity: shortId })) +
      '">' +
      HEART_SVG +
      "</button>" +
      "</div>" +
      '<a class="saved-items__copy" href="' +
      escapeHtml(href) +
      '">' +
      '<span class="saved-items__identity">' +
      escapeHtml(identity) +
      "</span>" +
      (grading ? '<span class="saved-items__grading">' + escapeHtml(grading) + "</span>" : "") +
      (price ? '<span class="saved-items__price">' + escapeHtml(price) + "</span>" : "") +
      (status
        ? '<span class="saved-items__status" data-availability="' +
          escapeHtml(model.availability || model.status || "") +
          '">' +
          escapeHtml(status) +
          "</span>"
        : "") +
      '<span class="saved-items__view">VIEW DIAMOND</span>' +
      "</a>" +
      "</article>"
    );
  }

  function renderCardFromItem(item, hydration) {
    return renderCardHtml(viewModel(item, hydration));
  }

  function renderDiamondsSection(items, hydrations) {
    items = displayItems(items);
    hydrations = hydrations || {};
    if (!items.length) return renderEmptyHtml();
    var cards = items
      .map(function (item) {
        var key = item && item.id ? String(item.id) : "";
        return "<li>" + renderCardFromItem(item, hydrations[key]) + "</li>";
      })
      .join("");
    return (
      '<section class="saved-items__provider" data-saved-provider="diamonds">' +
      '<h2 class="saved-items__provider-title">Diamonds</h2>' +
      '<ul class="saved-items__grid" data-saved-grid>' +
      cards +
      "</ul>" +
      "</section>"
    );
  }

  function renderPageHtml(items, hydrations) {
    items = Array.isArray(items) ? items : [];
    if (!items.length) return renderEmptyHtml();
    return renderIntroMetaHtml(items.length) + renderDiamondsSection(items, hydrations);
  }

  function mapWithConcurrency(items, worker, concurrency, options) {
    options = options || {};
    items = Array.isArray(items) ? items : [];
    var cap = typeof concurrency === "number" && concurrency > 0 ? concurrency : HYDRATION_CONCURRENCY;
    var results = new Array(items.length);
    var cursor = 0;
    var inflight = 0;
    var peak = 0;

    function stale() {
      return !!(options.signal && options.signal.aborted);
    }

    function next() {
      if (stale()) return Promise.resolve();
      if (cursor >= items.length) return Promise.resolve();
      var index = cursor;
      cursor += 1;
      inflight += 1;
      if (inflight > peak) peak = inflight;
      if (typeof options.onInflight === "function") options.onInflight(inflight, peak);
      return Promise.resolve()
        .then(function () {
          return worker(items[index], index);
        })
        .then(function (value) {
          results[index] = value;
        })
        .catch(function (err) {
          if (isAbortError(err)) throw err;
          results[index] = { item: items[index], status: "transient", diamond: null, error: err };
        })
        .then(function () {
          inflight -= 1;
          if (typeof options.onInflight === "function") options.onInflight(inflight, peak);
          return next();
        });
    }

    var starters = [];
    var n = Math.min(cap, items.length);
    var i;
    for (i = 0; i < n; i += 1) starters.push(next());
    if (!starters.length) {
      return Promise.resolve({ results: [], peak: 0 });
    }
    return Promise.all(starters).then(function () {
      return { results: results, peak: peak };
    });
  }

  function fetchDiamond(id, signal) {
    var api = global.IvanovAPI;
    if (!api || typeof api.getDiamond !== "function") {
      return Promise.resolve({ status: "transient", diamond: null });
    }
    var requestOptions = {};
    if (signal) requestOptions.signal = signal;
    return api
      .getDiamond(id, requestOptions)
      .then(function (data) {
        var diamond = data && data.diamond && typeof data.diamond === "object" ? data.diamond : null;
        if (!diamond) return { status: "transient", diamond: null };
        if (isUnavailable(diamond)) return { status: "unavailable", diamond: diamond };
        return { status: "current", diamond: diamond };
      })
      .catch(function (err) {
        if (isAbortError(err)) throw err;
        if (isGenuine404(err)) return { status: "gone", diamond: null, error: err };
        return { status: "transient", diamond: null, error: err };
      });
  }

  function hydrateItems(items, options) {
    options = options || {};
    items = Array.isArray(items) ? items.slice() : [];
    return mapWithConcurrency(
      items,
      function (item) {
        return fetchDiamond(item && item.id, options.signal).then(function (result) {
          result = result || {};
          if (result.status === "gone") {
            var Saved = savedApi();
            if (Saved && typeof Saved.remove === "function" && item && item.id) {
              Saved.remove(item.id);
            }
          }
          return {
            item: item,
            status: result.status || "transient",
            diamond: result.diamond || null,
          };
        });
      },
      options.concurrency || HYDRATION_CONCURRENCY,
      options
    ).then(function (mapped) {
      var hydrations = {};
      (mapped.results || []).forEach(function (result) {
        if (!result || !result.item || !result.item.id) return;
        if (result.status === "gone") return;
        hydrations[result.item.id] = result;
      });
      return { hydrations: hydrations, peak: mapped.peak, results: mapped.results || [] };
    });
  }

  function bindImageFallbacks(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;
    var images = root.querySelectorAll("[data-saved-image]");
    var list =
      images && typeof images.forEach === "function"
        ? images
        : Array.prototype.slice.call(images || []);
    list.forEach(function (img) {
      if (!img || img.__ivanovSavedItemsFallbackBound) return;
      img.__ivanovSavedItemsFallbackBound = true;
      if (typeof img.addEventListener !== "function") return;
      img.addEventListener("error", function () {
        img.hidden = true;
        var parent = img.parentNode;
        var fallback =
          parent && typeof parent.querySelector === "function"
            ? parent.querySelector("[data-saved-fallback]")
            : null;
        if (fallback) fallback.hidden = false;
      });
    });
  }

  function createController(root, options) {
    options = options || {};
    if (!root || root.__ivanovSavedItemsBound) return root && root.__ivanovSavedItemsController;
    var doc = options.document || global.document;
    var win = options.window || global;
    var content = root.querySelector("[data-saved-items-content]") || root;
    var cache = Object.create(null);
    var renderedKey = "";
    var runId = 0;
    var hydrating = false;

    function idsKey(items) {
      return (items || [])
        .map(function (item) {
          return item && item.id ? String(item.id) : "";
        })
        .join("|");
    }

    function paint(items, hydrations) {
      var html = renderPageHtml(items, hydrations);
      if (html === renderedKey) return;
      content.innerHTML = html;
      renderedKey = html;
      bindImageFallbacks(content);
    }

    function knownHydrations(items) {
      var out = Object.create(null);
      (items || []).forEach(function (item) {
        if (item && item.id && cache[item.id]) out[item.id] = cache[item.id];
      });
      return out;
    }

    function refreshFromStore(forceHydrate) {
      var items = currentItems();
      if (!items.length) {
        cache = Object.create(null);
        paint([], {});
        return Promise.resolve();
      }
      paint(items, knownHydrations(items));
      var missing = items.filter(function (item) {
        return item && item.id && (forceHydrate || !cache[item.id]);
      });
      if (!missing.length) return Promise.resolve();
      hydrating = true;
      runId += 1;
      var generation = runId;
      return hydrateItems(missing, { concurrency: HYDRATION_CONCURRENCY }).then(function (result) {
        if (generation !== runId) return;
        Object.keys(result.hydrations || {}).forEach(function (id) {
          cache[id] = result.hydrations[id];
        });
        var latest = currentItems();
        Object.keys(cache).forEach(function (id) {
          if (
            !latest.some(function (item) {
              return item && item.id === id;
            })
          ) {
            delete cache[id];
          }
        });
        paint(latest, knownHydrations(latest));
        hydrating = false;
      });
    }

    function onRootClick(event) {
      var target = event && event.target;
      var removeBtn = target && target.closest ? target.closest("[data-saved-remove]") : null;
      if (!removeBtn) return;
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (event && typeof event.stopPropagation === "function") event.stopPropagation();
      var Saved = savedApi();
      var id = removeBtn.getAttribute ? removeBtn.getAttribute("data-diamond-id") : "";
      if (Saved && typeof Saved.remove === "function" && id) Saved.remove(id);
    }

    function onChange(event) {
      var detail = (event && event.detail) || {};
      var action = detail.action;
      var id = detail.id ? canonicalId(detail.id) : "";
      var items = currentItems();
      if (action === "remove" && id) {
        delete cache[id];
        paint(items, knownHydrations(items));
        return;
      }
      if (action === "save" && id) {
        paint(items, knownHydrations(items));
        if (cache[id]) return;
        refreshFromStore(false);
        return;
      }
      var nextIds = idsKey(items);
      Object.keys(cache).forEach(function (cachedId) {
        if (nextIds.indexOf(cachedId) === -1) delete cache[cachedId];
      });
      refreshFromStore(false);
    }

    if (typeof root.addEventListener === "function") {
      root.addEventListener("click", onRootClick);
    }
    if (win && typeof win.addEventListener === "function") {
      win.addEventListener(CHANGE_EVENT, onChange);
    }

    refreshFromStore(true);

    var controller = {
      refresh: refreshFromStore,
      paint: paint,
      destroy: function () {
        if (win && typeof win.removeEventListener === "function") {
          win.removeEventListener(CHANGE_EVENT, onChange);
        }
      },
    };
    root.__ivanovSavedItemsBound = true;
    root.__ivanovSavedItemsController = controller;
    return controller;
  }

  function boot(options) {
    options = options || {};
    var doc = options.document || global.document;
    if (!doc || typeof doc.querySelectorAll !== "function") return [];
    var nodes = doc.querySelectorAll("[data-saved-items]");
    var list =
      nodes && typeof nodes.forEach === "function"
        ? nodes
        : Array.prototype.slice.call(nodes || []);
    var controllers = [];
    list.forEach(function (node) {
      controllers.push(createController(node, options));
    });
    return controllers;
  }

  function start() {
    boot();
  }

  if (global.document && global.document.readyState === "loading") {
    global.document.addEventListener("DOMContentLoaded", start);
  } else if (global.document) {
    start();
  }

  global.DiamondSavedItems = {
    PAGE_PATH: PAGE_PATH,
    SEARCH_PATH: SEARCH_PATH,
    DETAIL_PATH: DETAIL_PATH,
    CHANGE_EVENT: CHANGE_EVENT,
    HYDRATION_CONCURRENCY: HYDRATION_CONCURRENCY,
    formatIdentity: formatIdentity,
    formatShortIdentity: formatShortIdentity,
    formatGrading: formatGrading,
    formatPrice: formatPrice,
    formatRemoveLabel: formatRemoveLabel,
    formatCountLabel: formatCountLabel,
    detailUrl: detailUrl,
    availabilityState: availabilityState,
    isUnavailable: isUnavailable,
    isGenuine404: isGenuine404,
    isTransientFailure: isTransientFailure,
    resolveImage: resolveImage,
    leakedInternal: leakedInternal,
    displayItems: displayItems,
    viewModel: viewModel,
    renderEmptyHtml: renderEmptyHtml,
    renderCardHtml: renderCardHtml,
    renderCardFromItem: renderCardFromItem,
    renderDiamondsSection: renderDiamondsSection,
    renderPageHtml: renderPageHtml,
    mapWithConcurrency: mapWithConcurrency,
    hydrateItems: hydrateItems,
    createController: createController,
    boot: boot,
  };
})(typeof window !== "undefined" ? window : this);
